// Structured facts transcribed verbatim from a physical equipment label by
// the image-analysis prompt (AHA-6681). Rides along into the specs lookup so
// label statements outrank model-number pattern guesses, and can drive an
// evidence-only lookup when no model number is readable.
export type LabelEvidence = {
  brandText: string | null;
  unitDescriptor: string | null;
  seriesName: string | null;
  indoorOutdoorMarking: string | null;
  refrigerants: string[];
  manufactureDate: string | null;
  serialNumber: string | null;
  voltageText: string | null;
  amperageText: string | null;
  btuText: string | null;
  cfmText: string | null;
};

const STRING_FIELDS = [
  'brandText',
  'unitDescriptor',
  'seriesName',
  'indoorOutdoorMarking',
  'manufactureDate',
  'serialNumber',
  'voltageText',
  'amperageText',
  'btuText',
  'cfmText'
] as const;

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

// Tolerant read of the image-analysis response: older prompt versions (or a
// model ignoring the format) may omit any evidence field — missing/invalid
// fields land as null / [].
export function parseLabelEvidence(data: unknown): LabelEvidence | null {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const record = data as Record<string, unknown>;
  return {
    brandText: asNullableString(record.brandText),
    unitDescriptor: asNullableString(record.unitDescriptor),
    seriesName: asNullableString(record.seriesName),
    indoorOutdoorMarking: asNullableString(record.indoorOutdoorMarking),
    refrigerants: Array.isArray(record.refrigerants)
      ? record.refrigerants.filter(
          (entry): entry is string =>
            typeof entry === 'string' && entry.trim().length > 0
        )
      : [],
    manufactureDate: asNullableString(record.manufactureDate),
    serialNumber: asNullableString(record.serialNumber),
    voltageText: asNullableString(record.voltageText),
    amperageText: asNullableString(record.amperageText),
    btuText: asNullableString(record.btuText),
    cfmText: asNullableString(record.cfmText)
  };
}

export function hasUsableLabelEvidence(
  evidence: LabelEvidence | null | undefined
): evidence is LabelEvidence {
  if (!evidence) {
    return false;
  }
  return (
    STRING_FIELDS.some((field) => !!evidence[field]) ||
    evidence.refrigerants.length > 0
  );
}
